import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  Button,
  Form,
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@barfacil/barfacil-ui'
import { zodResolver } from '@hookform/resolvers/zod'
import { Pencil } from 'lucide-react'
import React, { useEffect, useState } from 'react'
import { useForm, useFormContext } from 'react-hook-form'

import { UpdateProductComboForm } from '@/app/panel/management/price-list/_components/update-product-combo-form'
import { UpdateProductGeralForm } from '@/app/panel/management/price-list/_components/update-product-geral-form'
import { UpdateProductNameConfirmationDialog } from '@/app/panel/management/price-list/_components/update-product-name-confirmation-dialog'
import { UpdateProductTaxForm } from '@/app/panel/management/price-list/_components/update-product-tax-form'
import { useUpdateProductContext } from '@/app/panel/management/price-list/_contexts/update-product-context'
import { usePriceListCategoryOptions } from '@/app/panel/management/price-list/_hooks/use-category-options'
import { useSyncTaxGroup } from '@/app/panel/management/price-list/_hooks/use-sync-tax-group'
import { useTaxList } from '@/app/panel/management/price-list/_hooks/use-tax-list'
import { useUpdateProduct } from '@/app/panel/management/price-list/_hooks/use-update-product'
import { useUpdateProductMapper } from '@/app/panel/management/price-list/_hooks/use-update-product-mapper'
import {
  FormUpdateProduct,
  FormUpdateProductSchema,
} from '@/app/panel/management/price-list/_schemas/update-product'
import {
  AccordionOptions,
  stepErrors,
  TabsOptions,
} from '@/app/panel/management/price-list/_schemas/update-product-types'
import { cn, resetBodyPointerEvents } from '@/lib/utils'

import { useProductInfo } from '../_hooks/use-product-info'

type UpdateProductAccordionProps = React.PropsWithChildren & {
  value: string
  onValueChange: (value: string) => void
  isTaxDisabled?: boolean
}

type UpdateProductTaxInfoProps = {
  disabled: boolean
}

type AccordionItemInfoProps = React.PropsWithChildren & {
  hasSectionError: boolean
}

const AccordionItemInfo = ({
  hasSectionError,
  children,
}: AccordionItemInfoProps) => (
  <div className="flex items-center gap-4 px-2">
    <aside className="flex flex-1 items-center gap-1">
      <h3
        className={cn(
          'line-clamp-2 text-sm group-data-[disabled]:text-gray-300',
          hasSectionError ? 'text-red-400' : 'text-blue-400',
        )}
      >
        {children}
      </h3>
    </aside>

    <aside className="flex items-center gap-4 sm:gap-2">
      <AccordionTrigger
        className={cn(
          'my-3 py-0 group-data-[disabled]:text-gray-300',
          hasSectionError ? 'text-red-500' : 'text-sky-500',
        )}
      />
    </aside>
  </div>
)

const UpdateProductGeralInfo = () => {
  const { product } = useUpdateProductContext()

  const form = useFormContext<FormUpdateProduct>()

  const hasSectionError = !!Object.entries(form.formState.errors).find(
    ([key]) =>
      stepErrors[key as keyof typeof stepErrors] === AccordionOptions.geral,
  )

  return (
    <AccordionItem
      className={cn(
        'rounded-md border bg-gray-25 px-2',
        hasSectionError ? 'border-red-200' : 'border-gray-200',
      )}
      value={AccordionOptions.geral}
    >
      <AccordionItemInfo hasSectionError={hasSectionError}>
        INFORMAÇÕES GERAIS
      </AccordionItemInfo>

      <AccordionContent className="px-2">
        <UpdateProductGeralForm product={product} />
      </AccordionContent>
    </AccordionItem>
  )
}

const UpdateProductTaxInfo = ({ disabled }: UpdateProductTaxInfoProps) => {
  const { product } = useUpdateProductContext()
  const { data: productWithTax } = useProductInfo(
    { id: product.cod_produto },
    !disabled,
  )

  const form = useFormContext<FormUpdateProduct>()

  const hasSectionError = !!Object.entries(form.formState.errors).find(
    ([key]) =>
      stepErrors[key as keyof typeof stepErrors] === AccordionOptions.tax,
  )

  const { syncTax } = useSyncTaxGroup()

  useEffect(() => {
    form.setValue(
      'pis_situacao_tributaria',
      productWithTax?.pis_situacao_tributaria ?? '',
    )

    form.setValue(
      'cofins_situacao_tributaria',
      productWithTax?.cofins_situacao_tributaria ?? '',
    )

    form.setValue(
      'grupo_tributario',
      productWithTax?.grupo_tributario?.cod_grupo_tributario?.toString(),
    )

    syncTax(form, productWithTax?.grupo_tributario)

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productWithTax, open])

  return (
    <AccordionItem
      className={cn(
        'group rounded-md border bg-gray-25 px-2 data-[disabled]:cursor-not-allowed data-[disabled]:bg-gray-100 data-[disabled]:text-gray-300',
        hasSectionError ? 'border-red-200' : 'border-gray-200',
      )}
      value={AccordionOptions.tax}
      disabled={disabled}
    >
      <AccordionItemInfo hasSectionError={hasSectionError}>
        INFORMAÇÕES FISCAIS
      </AccordionItemInfo>

      <AccordionContent className="px-2">
        <UpdateProductTaxForm />
      </AccordionContent>
    </AccordionItem>
  )
}

const UpdateProductComboInfo = () => {
  const form = useFormContext<FormUpdateProduct>()

  const hasSectionError = !!Object.entries(form.formState.errors).find(
    ([key]) =>
      stepErrors[key as keyof typeof stepErrors] === AccordionOptions.combo,
  )

  return (
    <AccordionItem
      className={cn(
        'rounded-md border bg-gray-25 px-2',
        hasSectionError ? 'border-red-200' : 'border-gray-200',
      )}
      value={AccordionOptions.combo}
    >
      <AccordionItemInfo hasSectionError={false}>
        PRODUTOS NO COMBO
      </AccordionItemInfo>

      <AccordionContent className="px-2">
        <UpdateProductComboForm />
      </AccordionContent>
    </AccordionItem>
  )
}

const UpdateProductAccordion = ({
  value,
  onValueChange,
  isTaxDisabled = false,
  children,
}: UpdateProductAccordionProps) => (
  <Accordion
    className="flex flex-1 flex-col gap-4"
    type="single"
    collapsible
    value={value}
    onValueChange={onValueChange}
  >
    <UpdateProductGeralInfo />
    <UpdateProductTaxInfo disabled={isTaxDisabled} />
    {children}
  </Accordion>
)

export const UpdateProductDialog = () => {
  const [openNameChangeConfirmation, setOpenNameChangeConfirmation] =
    useState(false)

  const [tabsValue, setTabsValue] = useState<string>(TabsOptions.product)
  const [accordionValue, setAccordionValue] = useState<string>(
    AccordionOptions.geral,
  )

  const { product, open, setOpen, comboProducts, setComboProducts } =
    useUpdateProductContext()
  const { mapEntityToForm, mapFormToPayload } = useUpdateProductMapper()
  const { mutation, formErrors } = useUpdateProduct()

  const { isLoading: isLoadingCategoryOptions } = usePriceListCategoryOptions()
  const { isLoading: cfopListLoading } = useTaxList('cfopList', open)
  const { isLoading: ncmListLoading } = useTaxList('ncmList', open)
  const { isLoading: icmsOrigemListLoading } = useTaxList(
    'icmsOrigemList',
    open,
  )
  const { isLoading: icmsSituacaoListLoading } = useTaxList(
    'icmsSituacaoList',
    open,
  )
  const { isLoading: icmsModalidadeListLoading } = useTaxList(
    'icmsModalidadeList',
    open,
  )
  const { isLoading: pisCofinsTipoListLoading } = useTaxList(
    'pisCofinsTipoList',
    open,
  )
  const { isLoading: pisCofinsSituacaoListLoading } = useTaxList(
    'pisCofinsSituacaoList',
    open,
  )

  const isLoading =
    isLoadingCategoryOptions ||
    cfopListLoading ||
    ncmListLoading ||
    icmsOrigemListLoading ||
    icmsSituacaoListLoading ||
    icmsModalidadeListLoading ||
    pisCofinsTipoListLoading ||
    pisCofinsSituacaoListLoading

  const form = useForm<FormUpdateProduct>({
    resolver: zodResolver(FormUpdateProductSchema),
    defaultValues: mapEntityToForm(product),
  })

  function onTabsValueChange(value: string) {
    setAccordionValue(AccordionOptions.geral)
    setTabsValue(value)
    form.setValue('tipo', value === TabsOptions.product ? 1 : 3)
  }

  function handleOnOpenChange(open: boolean) {
    if (open) {
      form.reset(mapEntityToForm(product))
      setComboProducts(product.produto_combo_list ?? [])
      setTabsValue(product.tipo === 3 ? TabsOptions.combo : TabsOptions.product)
      setAccordionValue(AccordionOptions.geral)
    } else {
      resetBodyPointerEvents()
    }

    setOpen(open)
  }

  async function handleSubmitConfirm(data: FormUpdateProduct) {
    await mutation.mutateAsync(mapFormToPayload(data, comboProducts), {
      onSuccess: () => setOpen(false),
    })
  }

  function handleSubmit<T>(e: React.FormEvent<T>) {
    e.preventDefault()

    return form.handleSubmit(async (data) => {
      const hasNameChanged = !!form.formState.dirtyFields.nome

      if (hasNameChanged) return setOpenNameChangeConfirmation(true)

      await handleSubmitConfirm(data)
    })()
  }

  useEffect(() => {
    if (!formErrors.length) return

    const minErrorOption = formErrors.reduce((acc, { key, messages }) => {
      const option =
        stepErrors[key as keyof typeof stepErrors] ?? AccordionOptions.geral

      form.setError(key as keyof FormUpdateProduct, {
        message: messages[0],
      })

      return acc === AccordionOptions.geral || option === AccordionOptions.geral
        ? AccordionOptions.geral
        : option
    }, AccordionOptions.tax)

    if (minErrorOption) setAccordionValue(minErrorOption)
  }, [form, formErrors])

  useEffect(() => {
    const errors = Object.entries(form.formState.errors)

    if (!errors.length) return

    const minErrorOption = errors.reduce((acc, [key, { message }]) => {
      const option =
        stepErrors[key as keyof typeof stepErrors] ?? AccordionOptions.geral

      form.setError(key as keyof FormUpdateProduct, {
        message,
      })

      return acc === AccordionOptions.geral || option === AccordionOptions.geral
        ? AccordionOptions.geral
        : option
    }, AccordionOptions.tax)

    if (minErrorOption) setAccordionValue(minErrorOption)
  }, [form, form.formState.errors])

  return (
    <Sheet open={open} onOpenChange={handleOnOpenChange}>
      <SheetTrigger asChild>
        <Button className="size-6" variant="ghost" size="icon">
          <Pencil size={20} />
        </Button>
      </SheetTrigger>

      <SheetContent className="flex w-11/12 flex-1 flex-col max-sm:overflow-auto sm:h-full sm:w-4/5 sm:max-w-full">
        <Form {...form}>
          <form id="update-product-form" onSubmit={handleSubmit} />

          <SheetHeader className="text-left">
            <SheetTitle>{product.nome}</SheetTitle>
            <SheetDescription hidden />
          </SheetHeader>

          <div className="flex flex-1 sm:overflow-auto">
            <Tabs
              className="my-4 h-min w-full rounded-md border border-gray-100 bg-gray-50"
              value={tabsValue}
              onValueChange={onTabsValueChange}
            >
              <TabsList className="grid w-full grid-cols-2 rounded-b-none">
                <TabsTrigger value={TabsOptions.product}>SIMPLES</TabsTrigger>
                <TabsTrigger value={TabsOptions.combo}>COMBO</TabsTrigger>
              </TabsList>

              <TabsContent className="m-0 p-4" value={TabsOptions.product}>
                <UpdateProductAccordion
                  value={accordionValue}
                  onValueChange={setAccordionValue}
                />
              </TabsContent>
              <TabsContent className="m-0 p-4" value={TabsOptions.combo}>
                <UpdateProductAccordion
                  value={accordionValue}
                  onValueChange={setAccordionValue}
                  isTaxDisabled
                >
                  <UpdateProductComboInfo />
                </UpdateProductAccordion>
              </TabsContent>
            </Tabs>
          </div>

          <SheetFooter className="max-sm:gap-2">
            <SheetClose asChild>
              <Button className="sm:w-32" variant="tertiary" type="button">
                CANCELAR
              </Button>
            </SheetClose>

            <Button
              className="sm:w-32"
              type="submit"
              form="update-product-form"
              disabled={mutation.isPending || isLoading}
              isLoading={mutation.isPending}
            >
              SALVAR
            </Button>
          </SheetFooter>

          <UpdateProductNameConfirmationDialog
            open={openNameChangeConfirmation}
            onOpenChange={setOpenNameChangeConfirmation}
            onSubmit={handleSubmitConfirm}
          />
        </Form>
      </SheetContent>
    </Sheet>
  )
}
